import { z } from "zod";

/**
 * Projektleiter-Weekly (Spec 2026-10-05): das wöchentliche Gespräch zwischen
 * Service Lead und Bereichsleitung. Ein Weekly gehört zu Lead × Segment und
 * einer ISO-Kalenderwoche.
 */

export interface IsoWeek {
  isoYear: number;
  isoWeek: number;
}

const TAG_MS = 24 * 60 * 60 * 1000;

/**
 * ISO-8601-Kalenderwoche eines Kalendertags (`YYYY-MM-DD`). Rechnet in UTC,
 * damit Sommerzeit nie einen Tag verschiebt — der Kalendertag selbst kommt
 * vom Aufrufer (`heuteInBerlin()`).
 */
export function isoWeekOf(date: string): IsoWeek {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`isoWeekOf: Datum im Format YYYY-MM-DD erwartet, bekam "${date}"`);
  const tag = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const wochentag = (new Date(tag).getUTCDay() + 6) % 7; // Mo = 0 … So = 6
  const donnerstag = tag + (3 - wochentag) * TAG_MS;
  const isoYear = new Date(donnerstag).getUTCFullYear();
  const jan4 = Date.UTC(isoYear, 0, 4);
  const ersterDonnerstag = jan4 + (3 - ((new Date(jan4).getUTCDay() + 6) % 7)) * TAG_MS;
  const isoWeek = 1 + Math.round((donnerstag - ersterDonnerstag) / (7 * TAG_MS));
  return { isoYear, isoWeek };
}

export function compareIsoWeek(a: IsoWeek, b: IsoWeek): number {
  return a.isoYear !== b.isoYear ? a.isoYear - b.isoYear : a.isoWeek - b.isoWeek;
}

/** Montag der ISO-Woche als `YYYY-MM-DD` (UTC-Rechnung, Ergebnis als Kalenderdatum). */
export function isoWeekMonday(w: IsoWeek): string {
  const jan4 = Date.UTC(w.isoYear, 0, 4);
  const montagKw1 = jan4 - ((new Date(jan4).getUTCDay() + 6) % 7) * TAG_MS;
  return new Date(montagKw1 + (w.isoWeek - 1) * 7 * TAG_MS).toISOString().slice(0, 10);
}

/** Die ISO-Woche davor (über den Jahreswechsel inkl. KW 53). */
export function vorigeIsoWoche(w: IsoWeek): IsoWeek {
  const montag = Date.parse(`${isoWeekMonday(w)}T00:00:00Z`);
  return isoWeekOf(new Date(montag - 7 * TAG_MS).toISOString().slice(0, 10));
}

export const WEEKLY_STATUSES = ["offen", "abgeschlossen"] as const;
export type WeeklyStatus = (typeof WEEKLY_STATUSES)[number];

export const WEEKLY_TODO_OWNERS = ["lead", "fuehrung"] as const;
export const weeklyTodoOwnerSchema = z.enum(WEEKLY_TODO_OWNERS);
export type WeeklyTodoOwner = z.infer<typeof weeklyTodoOwnerSchema>;

/** Obergrenze je Freitext — schützt vor versehentlich eingefügten Mengen, nicht vor Inhalt. */
export const WEEKLY_NOTES_MAX = 20_000;

export const weeklyNotesUpdateSchema = z.object({
  notes: z.string().max(WEEKLY_NOTES_MAX),
  version: z.number().int().min(0),
});
export type WeeklyNotesUpdateInput = z.infer<typeof weeklyNotesUpdateSchema>;

const nieBeideZeilen = (v: { customerId?: string | null; prospectId?: string | null }) =>
  !(v.customerId && v.prospectId);

/** Felder ohne die Zeilenregel — die Werkzeuge der MCP-Schicht erweitern das Objekt; die Route prüft die Regel. */
export const createWeeklyTodoFieldsSchema = z.object({
  text: z.string().trim().min(1).max(500),
  owner: weeklyTodoOwnerSchema,
  customerId: z.string().uuid().nullish(),
  prospectId: z.string().uuid().nullish(),
});
export const createWeeklyTodoSchema = createWeeklyTodoFieldsSchema.refine(nieBeideZeilen, { message: "Ein Todo gehört zu höchstens einer Zeile." });
export type CreateWeeklyTodoInput = z.infer<typeof createWeeklyTodoSchema>;

export const updateWeeklyTodoFieldsSchema = z.object({
  text: z.string().trim().min(1).max(500).optional(),
  owner: weeklyTodoOwnerSchema.optional(),
  customerId: z.string().uuid().nullish(),
  prospectId: z.string().uuid().nullish(),
  done: z.boolean().optional(),
});
export const updateWeeklyTodoSchema = updateWeeklyTodoFieldsSchema.refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "Mindestens ein Feld ändern.",
  })
  .refine(nieBeideZeilen, { message: "Ein Todo gehört zu höchstens einer Zeile." });
export type UpdateWeeklyTodoInput = z.infer<typeof updateWeeklyTodoSchema>;

export const createWeeklyProspectSchema = z.object({
  name: z.string().trim().min(1).max(200),
});
export type CreateWeeklyProspectInput = z.infer<typeof createWeeklyProspectSchema>;

export const updateWeeklyProspectFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  archived: z.boolean().optional(),
});
export const updateWeeklyProspectSchema = updateWeeklyProspectFieldsSchema.refine((v) => v.name !== undefined || v.archived !== undefined, {
    message: "Mindestens ein Feld ändern.",
  });
export type UpdateWeeklyProspectInput = z.infer<typeof updateWeeklyProspectSchema>;

/** ------------------------------------------------------------ Kennzahlen (Scheibe 3) */

/** Spec 2026-10-05, Kennzahlen: Restbudget unter diesem Prozentwert wird hervorgehoben. */
export const WEEKLY_BUDGET_LOW_PERCENT = 20;
/** Spec 2026-10-05, Kennzahlen: Projektende in weniger als so vielen Tagen wird hervorgehoben. */
export const WEEKLY_PROJECT_END_SOON_DAYS = 60;
/**
 * #333: Bei einer Pauschale zählt der Auftragszeitraum, nicht das Restbudget.
 * Endet sie in weniger als so vielen Tagen, ist sie markiert — früher als ein
 * Kontingent, weil die Folgepauschale für den nächsten Zeitraum erst angelegt
 * werden muss.
 */
export const WEEKLY_FLAT_FEE_END_SOON_DAYS = 90;

/**
 * Block der Budgetübersicht (#333), abgeleitet aus der Salesforce-Abrechnungsart
 * (`service_projects.billing_type`): „Rechnung" rechnet gegen ein Kontingent ab,
 * „Festpreis" ist eine Pauschale, alles andere (auch leer) steht unter „Sonstige",
 * damit nichts verschwindet. Dieselben zwei Werte trennt die KPI-Umsatzaufteilung.
 */
export type WeeklyBillingKind = "kontingent" | "pauschale" | "sonstige";

export function weeklyBillingKind(billingType: string | null): WeeklyBillingKind {
  const wert = (billingType ?? "").trim().toLowerCase();
  if (wert === "rechnung") return "kontingent";
  if (wert === "festpreis") return "pauschale";
  return "sonstige";
}
/** Spec 2026-10-05, Kennzahlen: rollierendes Zeitfenster der Ticket-Durchschnitte in Tagen. */
export const WEEKLY_TICKET_WINDOW_DAYS = 90;
/** Spec 2026-10-05, Kennzahlen: Anzahl offener Tickets ohne Aktivität, die gelistet werden. */
export const WEEKLY_STALE_TOP_N = 3;
/** Spec 2026-10-05, Kennzahlen: Datenstand älter als so viele Stunden wird als veraltet markiert. */
export const WEEKLY_DATA_STALE_HOURS = 48;

export interface WeeklyBudgetRow {
  customerId: string;
  customerName: string;
  salesforceProjectId: string;
  projectNumber: string;
  title: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  budgetEur: number | null;
  expectedRevenueEur: number | null;
  budgetLeftEur: number | null;
  budgetLeftPercent: number | null; // 0..100+, null ohne Budget oder Budget <= 0
  lowBudget: boolean;
  endingSoon: boolean;
  /**
   * Ab #333. Fehlt in Snapshots, die vorher eingefroren wurden — die Oberfläche
   * zeigt solche Weeklies weiter als eine Tabelle.
   */
  billingKind?: WeeklyBillingKind;
  /** Kalendertage bis Projektende (negativ = überschritten). Ab #333. */
  daysUntilEnd?: number;
  /**
   * #307: Salesforce-Id des Hauptprojekts, unter dem diese Zeile eingeklappt
   * steht — nur, wenn das Hauptprojekt selbst in der Übersicht steht. Sonst null.
   */
  parentSalesforceProjectId?: string | null;
  /**
   * #307: Am Hauptprojekt mit Unterprojekten der ganze Topf (Budget und Rest
   * aller Familienmitglieder, gleiche Summenregel wie die Gesamtsumme).
   * `budgetLeftPercent`, `lowBudget` dieser Zeile rechnen dann aus dem Topf.
   */
  pot?: WeeklyBudgetTotal & { members: number };
}

export interface WeeklyBudgetTotal {
  budgetEur: number;
  expectedRevenueEur: number;
  budgetLeftEur: number;
  budgetLeftPercent: number | null;
}

export interface WeeklyBudget {
  rows: WeeklyBudgetRow[];
  total: WeeklyBudgetTotal;
}

const runde2 = (x: number) => Math.round(x * 100) / 100;

/**
 * Summen der Budgetübersicht. Budget und Restbudget summieren nur Projekte, bei
 * denen **beide** Werte gesetzt sind (Salesforce liefert sie unabhängig
 * voneinander; ein Projekt mit Budget, aber ohne Restbudget würde sonst als
 * „voll verbraucht" zählen). Der erwartete Umsatz zählt, wann immer er da ist.
 * Liegt in `@msc/shared`, weil die Oberfläche damit die Blocksummen rechnet.
 */
export function summiereBudget(
  rows: Array<Pick<WeeklyBudgetRow, "budgetEur" | "budgetLeftEur" | "expectedRevenueEur">>,
): WeeklyBudgetTotal {
  let budgetEur = 0;
  let budgetLeftEur = 0;
  let expectedRevenueEur = 0;
  for (const r of rows) {
    expectedRevenueEur += r.expectedRevenueEur ?? 0;
    if (r.budgetEur === null || r.budgetLeftEur === null) continue;
    budgetEur += r.budgetEur;
    budgetLeftEur += r.budgetLeftEur;
  }
  return {
    budgetEur: runde2(budgetEur),
    budgetLeftEur: runde2(budgetLeftEur),
    expectedRevenueEur: runde2(expectedRevenueEur),
    budgetLeftPercent: budgetEur > 0 ? runde2((budgetLeftEur / budgetEur) * 100) : null,
  };
}

export interface WeeklyAverage {
  avgMs: number | null;
  n: number;
}

export interface WeeklyStaleTicket {
  key: string;
  summary: string;
  status: string | null;
  daysSinceActivity: number;
}

export interface WeeklyTicketStats {
  openCount: number;
  staleTop: WeeklyStaleTicket[];
  firstResponse: WeeklyAverage;
  resolution: WeeklyAverage;
}

export type WeeklyCustomerTickets = { mapped: false } | ({ mapped: true } & WeeklyTicketStats);

/**
 * Vor-Ort-Termine einer Kundenzeile (#304), aus derselben Rechnung wie die
 * Terminseite (`erinnerungenFuerZellen`). `exempt` = freigegebene Ausnahme,
 * `none` = für die Zelle ist keine Frequenz hinterlegt.
 */
export interface WeeklyVisitState {
  status: "green" | "orange" | "red" | "none" | "exempt";
  lastVisitDate: string | null;
  nextPlannedDate: string | null;
  /** Frist „in Klärung“ (#351). Fehlt in Weeklies, die vorher abgeschlossen wurden. */
  clarifyUntil?: string | null;
  frequencyMonths: number | null;
}

/**
 * Wartungslage einer Kundenzeile (#304): Systeme an den eigenen
 * Beauftragungen, davon mit roter bzw. oranger Ampel (je System die
 * dringlichste) und mit veraltetem Stand. `attention` zählt jedes System mit
 * mindestens einem davon einmal. Kunde ohne Systeme: kein Eintrag.
 */
export interface WeeklyMaintenanceState {
  systems: number;
  attention: number;
  red: number;
  orange: number;
  stale: number;
}

export interface WeeklyMetrics {
  asOf: string; // ISO-Zeitpunkt der Berechnung
  sfSyncedAt: string | null;
  jiraSyncedAt: string | null;
  firstResponseMode: "sla" | "kalender";
  budget: WeeklyBudget;
  tickets: { total: WeeklyTicketStats; byCustomer: Record<string, WeeklyCustomerTickets> };
  /** Fehlt in Weeklies, die vor #304 abgeschlossen wurden. */
  visits?: Record<string, WeeklyVisitState>;
  /** Fehlt in Weeklies, die vor #304 abgeschlossen wurden. */
  maintenance?: Record<string, WeeklyMaintenanceState>;
}

/** ------------------------------------------------------------ DTOs */

export interface WeeklySummary {
  id: string;
  leadEmployeeId: string;
  leadName: string;
  segmentId: string;
  segmentName: string;
  isoYear: number;
  isoWeek: number;
  status: WeeklyStatus;
  closedAt: string | null;
  closedByName: string | null;
}

/**
 * Kunde (vom Lead verantwortet), Freizeile, oder `former`: ehemaliger Kunde
 * bzw. archivierte Freizeile mit Restinhalt (Notiz in diesem Weekly oder
 * sichtbares Todo). Eine ehemalige Freizeile hat `customerId: null`.
 */
export interface WeeklyRow {
  kind: "customer" | "prospect" | "former";
  customerId: string | null;
  prospectId: string | null;
  name: string;
  notes: string;
  notesVersion: number;
}

export interface WeeklyTodo {
  id: string;
  text: string;
  owner: WeeklyTodoOwner;
  customerId: string | null;
  prospectId: string | null;
  /** KW, in der das Todo entstand („offen seit KW …"). */
  createdIn: IsoWeek;
  /** In DIESEM Weekly erledigt (durchgestrichen). */
  done: boolean;
}

export interface WeeklyDetail {
  weekly: WeeklySummary & { generalNotes: string; generalNotesVersion: number };
  rows: WeeklyRow[];
  todos: WeeklyTodo[];
  permissions: { canWrite: boolean; canReopen: boolean };
  /** Live (offen) bzw. eingefroren (abgeschlossen); `null` = keine Kennzahlen eingefroren. */
  metrics: WeeklyMetrics | null;
  /** Basis-URLs für Links; fehlt eine, zeigt das Frontend nur Text. */
  links: { jiraBrowseBaseUrl: string | null; salesforceRecordBaseUrl: string | null };
}

export interface WeeklyLead {
  employeeId: string;
  fullName: string;
  segmentId: string;
  segmentName: string;
}

export interface WeeklyHistory {
  items: WeeklySummary[];
  currentWeek: IsoWeek;
  canCreate: boolean;
}

export interface WeeklyNoteSaved {
  notes: string;
  version: number;
}

/**
 * Eingefroren beim Abschließen: genau die Zeilenliste, die live angezeigt
 * wurde (inkl. `former`-Zeilen), ab Scheibe 3 dazu die Kennzahlen.
 * Gespiegelt in `@msc/db` (schema/weekly.ts); apps/api prüft beide Typen
 * gegeneinander.
 */
export interface WeeklySnapshotV1 {
  schemaVersion: 1;
  computedAt: string;
  rows: Array<Pick<WeeklyRow, "kind" | "customerId" | "prospectId" | "name">>;
  /** Scheibe 3; fehlt bei vor Scheibe 3 abgeschlossenen Weeklies. */
  metrics?: WeeklyMetrics;
}

/**
 * Übersicht für die Führungskraft (Spec 2026-10-05, Übersicht): Betrachtungsfenster
 * in ISO-Kalenderwochen vor der aktuellen KW — die aktuelle KW zählt nicht mit.
 */
export const WEEKLY_OVERVIEW_WEEKS = 8;

/**
 * Eine Zeile der Übersicht (Spec 2026-10-05, Übersicht): ein Lead × Segment.
 * Keine Abwesenheitslogik (Entscheidung 2026-10-07): jede Woche ohne Weekly ist
 * eine Lücke.
 */
export interface WeeklyOverviewRow {
  employeeId: string;
  fullName: string;
  segmentId: string;
  segmentName: string;
  lastWeekly: {
    id: string;
    isoYear: number;
    isoWeek: number;
    status: WeeklyStatus;
    closedAt: string | null;
  } | null;
  openWeeklyId: string | null;
  openTodos: { lead: number; fuehrung: number };
  /** Lücken im Fenster, neueste zuerst. Leer, wenn es noch kein Weekly gibt. */
  luecken: IsoWeek[];
  /** Anzahl Lücken (= luecken.length). */
  fehlendeWochen: number;
}
